import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { LangContext } from "@/context";
import { ROUTES } from "@/i18n";
import { Header, Footer, WhatsAppFloat } from "@/components/Layout";
import { CookieBanner, GuidePopup } from "@/components/Widgets";
import Home from "@/pages/Home";
import Listings from "@/pages/Listings";
import PropertyDetail from "@/pages/PropertyDetail";
import { Services, ServiceDetail } from "@/pages/Services";
import { Zones, ZoneDetail } from "@/pages/Zones";
import { Guides, ArticleDetail } from "@/pages/Guides";
import Tools from "@/pages/Tools";
import Admin from "@/pages/Admin";
import {
  HowItWorks, About, TestimonialsPage, FaqPage, ContactPage,
  SellPage, FreeGuidePage, FarmlandPage, LegalPage, NotFound,
} from "@/pages/StaticPages";

function Site({ lang }) {
  const location = useLocation();
  const R = ROUTES[lang];

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <LangContext.Provider value={{ lang }}>
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path={R.properties} element={<Listings />} />
            <Route path={R.beachfront} element={<Listings preset="beachfront" />} />
            <Route path={R.land} element={<Listings preset="land" />} />
            <Route path={R.houses} element={<Listings preset="houses" />} />
            <Route path={R.hospitality} element={<Listings preset="hospitality" />} />
            <Route path={R.sold} element={<Listings preset="sold" />} />
            <Route path={`${R.property}/:slug`} element={<PropertyDetail />} />
            <Route path={R.farmland} element={<FarmlandPage />} />
            <Route path={R.services} element={<Services />} />
            <Route path={`${R.service}/:slug`} element={<ServiceDetail />} />
            <Route path={R.zones} element={<Zones />} />
            <Route path={`${R.zone}/:slug`} element={<ZoneDetail />} />
            <Route path={R.guides} element={<Guides />} />
            <Route path={`${R.article}/:slug`} element={<ArticleDetail />} />
            <Route path={R.freeGuide} element={<FreeGuidePage />} />
            <Route path={R.tools} element={<Tools />} />
            <Route path={R.howItWorks} element={<HowItWorks />} />
            <Route path={R.about} element={<About />} />
            <Route path={R.testimonials} element={<TestimonialsPage />} />
            <Route path={R.faq} element={<FaqPage />} />
            <Route path={R.contact} element={<ContactPage />} />
            <Route path={R.sell} element={<SellPage />} />
            <Route path={R.privacy} element={<LegalPage kind="privacy" />} />
            <Route path={R.cookies} element={<LegalPage kind="cookies" />} />
            <Route path={R.terms} element={<LegalPage kind="terms" />} />
            <Route path={R.risks} element={<LegalPage kind="risks" />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
        <WhatsAppFloat />
        <CookieBanner />
        <GuidePopup />
      </div>
    </LangContext.Provider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin/*" element={<Admin />} />
        <Route path="/en/*" element={<Site lang="en" />} />
        <Route path="/*" element={<Site lang="it" />} />
      </Routes>
      <Toaster position="bottom-center" />
    </BrowserRouter>
  );
}

export default App;
